import {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    PayPalCardFieldsProvider,
    PayPalCardNumberField,
    PayPalCardExpiryField,
    PayPalCardCvvField,
    usePayPalCardFieldsOneTimePaymentSession
} from '@paypal/react-paypal-js/sdk-v6'
import {PoweredByPaypal} from './PaymentMethodMarks'
import '../../../styles/paypal.css'

const propTypes = {
    amount: PropTypes.shape({
        value: PropTypes.string,
        currencyCode: PropTypes.string
    }).isRequired,
    payLabel: PropTypes.string.isRequired,
    footnote: PropTypes.string,
    createOrder: PropTypes.func.isRequired,
    onApprove: PropTypes.func.isRequired,
    onFailure: PropTypes.func.isRequired
}

// The inputs are PayPal-hosted iframes, so the type inside them can only be styled through
// this object. Everything around them (the bordered box, the label, the focus ring) is ours,
// which is why the input itself is drawn with no chrome of its own.
const CARD_FIELD_STYLE = {
    input: {
        fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
        fontSize: '16px',
        color: '#2c2c30',
        padding: '0',
        height: '26px',
        border: 'none',
        outline: 'none',
        background: 'transparent'
    },
    'input::placeholder': {
        color: '#b9b9bd'
    }
}

/**
 * The card branch of the PayPal page: PayPal's embedded ("advanced") card fields plus the pay
 * button that submits them. Both live inside a single {@link PayPalCardFieldsProvider} because
 * the fields and the submit call share one card-fields session.
 *
 * The fields block and the footer are rendered as siblings rather than nested, so the footer
 * still falls to the bottom of the surrounding `.box` flex column like every other page.
 */
const PaypalCardSection = ({amount, payLabel, footnote, createOrder, onApprove, onFailure}) => {
    const [fieldsValid, setFieldsValid] = useState(false)

    const handleValidityChange = ({data}) => {
        setFieldsValid(Boolean(data?.number?.isValid && data?.expiry?.isValid && data?.cvv?.isValid))
    }

    return (
        <PayPalCardFieldsProvider amount={amount} validitychange={handleValidityChange}>
            <CardFieldsForm
                fieldsValid={fieldsValid}
                payLabel={payLabel}
                footnote={footnote}
                createOrder={createOrder}
                onApprove={onApprove}
                onFailure={onFailure}
            />
        </PayPalCardFieldsProvider>
    )
}

const CardFieldsForm = ({fieldsValid, payLabel, footnote, createOrder, onApprove, onFailure}) => {
    const {submit, submitResponse, error} = usePayPalCardFieldsOneTimePaymentSession()
    const [submitting, setSubmitting] = useState(false)

    // Held in a ref so the effects below react to a new submit outcome only, and not to the
    // parent handing down fresh callback identities.
    const callbacks = useRef({onApprove, onFailure})
    callbacks.current = {onApprove, onFailure}

    // submit() reports its outcome through state rather than by resolving or throwing, so the
    // approve/decline branches are handled here instead of at the call site.
    useEffect(() => {
        if (!submitResponse) return

        setSubmitting(false)
        const {state, data} = submitResponse

        if (state === 'succeeded') {
            callbacks.current.onApprove(data.orderId)
        } else if (state === 'canceled') {
            callbacks.current.onFailure('Authentication canceled. Please try again.')
        } else {
            callbacks.current.onFailure(data?.message || 'Payment failed. Check your card details and try again.')
        }
    }, [submitResponse])

    useEffect(() => {
        if (!error) return
        setSubmitting(false)
        callbacks.current.onFailure(error.message)
    }, [error])

    const handlePay = () => {
        setSubmitting(true)
        submit(createOrder().then((order) => order.orderId))
    }

    return (
        <>
            <div className="pp-card-fields">
                <div className="pp-field">
                    <div className="pp-field-label">Card Number</div>
                    <PayPalCardNumberField
                        containerClassName="pp-field-input"
                        placeholder="0000 0000 0000 0000"
                        style={CARD_FIELD_STYLE}
                    />
                </div>
                <div className="pp-field-row">
                    <div className="pp-field">
                        <div className="pp-field-label">Expiry Date</div>
                        <PayPalCardExpiryField
                            containerClassName="pp-field-input"
                            placeholder="MM/YY"
                            style={CARD_FIELD_STYLE}
                        />
                    </div>
                    <div className="pp-field">
                        <div className="pp-field-label">CVC</div>
                        <PayPalCardCvvField
                            containerClassName="pp-field-input"
                            placeholder="123"
                            style={CARD_FIELD_STYLE}
                        />
                    </div>
                </div>
                <PoweredByPaypal/>
            </div>

            <div className="pp-footer">
                <button
                    type="button"
                    className="pp-cta"
                    disabled={!fieldsValid || submitting}
                    onClick={handlePay}
                >
                    {submitting ? 'Processing…' : payLabel}
                </button>
                {footnote ? <div className="pp-footnote">{footnote}</div> : null}
            </div>
        </>
    )
}

PaypalCardSection.propTypes = propTypes

export default PaypalCardSection
